import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { childrenService, tasksService, completionsService, rewardsService, challengesService, getImageUrl } from '../services';
import api from '../services/api';
import Notifications from '../components/Notifications';
import ParentOnboarding from '../components/ParentOnboarding';
import EventsCalendarTab from '../components/EventsCalendarTab';

import './ParentDashboard.css';

const parseApiDateAsUtc = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return new Date(value);

  // Compatibilidade com timestamps legados sem timezone explícito.
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  return new Date(hasTimezone ? value : `${value}Z`);
};


export function ParentDashboard() {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [showNewChildForm, setShowNewChildForm] = useState(false);
  const [newChild, setNewChild] = useState({ name: '', pin: '', birthdate: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [, setUserProfile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadUserProfile();
    loadChildren();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await api.get('/auth/me');
      const user = response.data;
      setUserProfile(user);
      
      // Mostra onboarding se ainda não foi completado
      if (!user.onboarding_completed) {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error('Erro ao carregar perfil:', err);
    }
  };

  const handleCompleteOnboarding = async () => {
    try {
      await api.put('/auth/complete-onboarding');
      setShowOnboarding(false);
      setUserProfile(prev => ({ ...prev, onboarding_completed: true }));
    } catch (err) {
      console.error('Erro ao completar onboarding:', err);
      // Mesmo com erro, fecha o onboarding para não bloquear o usuário
      setShowOnboarding(false);
    }
  };

  const loadChildren = async () => {
    try {
      const response = await childrenService.getChildren();
      setChildren(response.data.children);
      if (response.data.children.length > 0) {
        setSelectedChild(response.data.children[0]);
      }
    } catch (err) {
      setError('Erro ao carregar crianças');
    }
  };

  const handleCreateChild = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await childrenService.createChild(newChild.name, newChild.pin, newChild.birthdate);
      setNewChild({ name: '', pin: '', birthdate: '' });
      setShowNewChildForm(false);
      await loadChildren();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar criança');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/parent-login');
  };

  return (
    <div className="dashboard">
      {/* Onboarding Wizard */}
      {showOnboarding && (
        <ParentOnboarding onComplete={handleCompleteOnboarding} />
      )}

      <div className="dashboard-header">
        <h1>🦸 Painel - Responsável</h1>
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          <Notifications />
          <button className="logout-btn" onClick={handleLogout}>
            Sair
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="dashboard-container">
        <div className="sidebar">
          <h2>Minhas Crianças</h2>
          <div className="children-list">
            {children.map((child) => (
              <div
                key={child.id}
                className={`child-item ${selectedChild?.id === child.id ? 'active' : ''}`}
                onClick={() => setSelectedChild(child)}
              >
                <strong>{child.name}</strong>
                <small>⭐ {child.available_points} HeroCoins</small>
              </div>
            ))}
          </div>

          <button
            className="add-child-btn"
            onClick={() => setShowNewChildForm(!showNewChildForm)}
          >
            + Adicionar Criança
          </button>

          <button
            className="add-child-btn"
            onClick={() => {
              if (selectedChild) {
                navigate(`/parent/study-missions/create?childId=${selectedChild.id}`);
              } else {
                alert('Selecione uma criança primeiro');
              }
            }}
            style={{ marginTop: '0.5rem', backgroundColor: '#6a5acd' }}
          >
            📚 Criar Missão de Estudo
          </button>

          {showNewChildForm && (
            <form onSubmit={handleCreateChild} className="form-inline">
              <input
                type="text"
                placeholder="Nome da criança"
                value={newChild.name}
                onChange={(e) => setNewChild({ ...newChild, name: e.target.value })}
                required
              />
              <div className="date-input-wrapper">
                <label htmlFor="child-birthdate" style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', display: 'block' }}>
                  Data de Nascimento
                </label>
                <input
                  id="child-birthdate"
                  type="date"
                  value={newChild.birthdate}
                  onChange={(e) => setNewChild({ ...newChild, birthdate: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <input
                type="text"
                placeholder="PIN (4 dígitos)"
                value={newChild.pin}
                onChange={(e) =>
                  setNewChild({ ...newChild, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })
                }
                maxLength="4"
                required
              />
              <button type="submit" disabled={loading}>
                Criar
              </button>
            </form>
          )}
        </div>

        <div className="main-content">
          {selectedChild ? (
            <ChildPanel child={selectedChild} allChildren={children} />
          ) : (
            <div className="empty-state">
              <p>Adicione uma criança para começar!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChildPanel({ child, allChildren }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [taskCompletions, setTaskCompletions] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [activeTab, setActiveTab] = useState('tasks');
  const [searchParams] = useSearchParams();
  const [showNewTaskForm, setShowNewTaskForm] = useState(false);
  const [showNewRewardForm, setShowNewRewardForm] = useState(false);
  const [showNewChallengeForm, setShowNewChallengeForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [showEditRewardForm, setShowEditRewardForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showEditTaskForm, setShowEditTaskForm] = useState(false);
  const [taskFilter, setTaskFilter] = useState('pending');
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pairingCode, setPairingCode] = useState(null);
  const [pairingCodeExpiry, setPairingCodeExpiry] = useState(null);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    points: 10,
    category: 'CASA',
    frequency: 'DAILY',
    parent_review_only: false,
  });
  const [newChallenge, setNewChallenge] = useState({
    title: '',
    description: '',
    points: 50,
  });
  const [newReward, setNewReward] = useState({
    title: '',
    description: '',
    cost_points: 50,
    min_age: null,
    selectedChildren: [], // IDs das crianças selecionadas
  });

  useEffect(() => {
    console.log('🔄 useEffect disparado - child.id:', child.id);
    const loadData = async () => {
      try {
        console.log('📡 Iniciando carregamento de dados...');
        const completionsResponse = await loadTaskCompletions();
        console.log('✅ Completions carregadas:', completionsResponse);
        await loadTasks(completionsResponse);
        console.log('✅ Tasks carregadas');
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error);
      }
    };
    if (child && child.id) {
      loadData();
    } else {
      console.warn('⚠️ child ou child.id não definido');
    }
    loadPendingCompletions();
    loadPendingRedemptions();
    loadRewards();
    loadChallenges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child.id]);

  useEffect(() => {
    // Lê query params e muda de aba automaticamente
    const tabParam = searchParams.get('tab');
    if (tabParam && ['tasks', 'review', 'rewards', 'challenges', 'redemptions', 'pairing', 'calendar'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const loadTasks = async (completionsData = null) => {
    try {
      console.log('🔍 loadTasks chamado para child:', child.id);
      const response = await tasksService.getTasks(child.id);
      console.log('📋 Tasks recebidas:', response.data.tasks);
      
      // Usa as completions passadas por parâmetro ou o estado atual
      const completionsToUse = completionsData || taskCompletions;

      const getCurrentWeekStart = () => {
        const now = new Date();
        const weekStart = new Date(now);
        const weekday = now.getDay(); // Sunday=0 ... Saturday=6
        const diffToMonday = weekday === 0 ? -6 : 1 - weekday;
        weekStart.setDate(now.getDate() + diffToMonday);
        weekStart.setHours(0, 0, 0, 0);
        return weekStart;
      };

      const getCurrentDayStart = () => {
        const dayStart = new Date();
        dayStart.setHours(0, 0, 0, 0);
        return dayStart;
      };

      const getApprovedCompletionForTask = (task) => {
        const approved = completionsToUse
          .filter((c) => c.task_id === task.id && c.status === 'APPROVED')
          .sort((a, b) => parseApiDateAsUtc(b.created_at) - parseApiDateAsUtc(a.created_at));

        if (approved.length === 0) return null;

        if (task.frequency === 'ONE_TIME') {
          return approved[0];
        }

        if (task.frequency === 'WEEKLY') {
          const weekStart = getCurrentWeekStart();
          return approved.find((c) => parseApiDateAsUtc(c.created_at) >= weekStart) || null;
        }

        // DAILY (default)
        const dayStart = getCurrentDayStart();
        return approved.find((c) => parseApiDateAsUtc(c.created_at) >= dayStart) || null;
      };
      
      // Ordena tarefas: concluídas mais recentes primeiro, depois não concluídas
      const sortedTasks = response.data.tasks.sort((a, b) => {
        const completionA = getApprovedCompletionForTask(a);
        const completionB = getApprovedCompletionForTask(b);
        
        // Se ambas concluídas, ordena por data de conclusão (mais recente primeiro)
        if (completionA && completionB) {
          return parseApiDateAsUtc(completionB.created_at) - parseApiDateAsUtc(completionA.created_at);
        }
        // Se apenas A concluída, A vem primeiro
        if (completionA) return -1;
        // Se apenas B concluída, B vem primeiro
        if (completionB) return 1;
        // Se nenhuma concluída, mantém ordem original
        return 0;
      });
      setTasks(sortedTasks);
    } catch (err) {
      console.error('Erro ao carregar tarefas:', err);
    }
  };

  const loadTaskCompletions = async () => {
    try {
      const response = await tasksService.getTaskCompletions(child.id);
      const completions = response.data.completions || [];
      setTaskCompletions(completions);
      return completions; // Retorna os dados para uso imediato
    } catch (err) {
      console.error('Erro ao carregar conclusões de tarefas:', err);
      return [];
    }
  };

  const loadPendingCompletions = async () => {
    try {
      const response = await completionsService.getCompletions('PENDING_PARENT');
      setCompletions(response.data.completions);
    } catch (err) {
      console.error('Erro ao carregar conclusões:', err);
    }
  };

  const loadPendingRedemptions = async () => {
    try {
      const response = await rewardsService.getRedemptions('PENDING_DELIVERY');
      // Filtra apenas os resgates da criança selecionada
      const childRedemptions = response.data.redemptions.filter(r => r.child_id === child.id);
      setRedemptions(childRedemptions);
    } catch (err) {
      console.error('Erro ao carregar resgates:', err);
    }
  };

  const loadRewards = async () => {
    try {
      const response = await rewardsService.getRewards();
      setRewards(response.data.rewards);
    } catch (err) {
      console.error('Erro ao carregar recompensas:', err);
    }
  };

  const loadChallenges = async () => {
    try {
      const response = await challengesService.getChallenges();
      setChallenges(response.data.challenges);
    } catch (err) {
      console.error('Erro ao carregar desafios:', err);
    }
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    try {
      if (!child || !child.id) {
        alert('Erro: Nenhuma criança selecionada');
        return;
      }
      
      await challengesService.createChallenge(
        child.id,
        newChallenge.title,
        newChallenge.description,
        parseInt(newChallenge.points, 10)
      );
      setNewChallenge({ title: '', description: '', points: 50 });
      setShowNewChallengeForm(false);
      await loadChallenges();
      alert('Desafio enviado para ' + child.name + '!');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Erro ao criar desafio';
      console.error('Erro ao criar desafio:', err);
      alert(errorMsg);
    }
  };

  const handleAcceptChallenge = async (challengeId) => {
    try {
      await challengesService.acceptChallenge(challengeId);
      await loadChallenges();
      await loadTasks();
      alert('Desafio aceito! Tarefa criada.');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao aceitar desafio');
    }
  };

  const handleRejectChallenge = async (challengeId) => {
    if (!window.confirm('Tem certeza que deseja rejeitar este desafio?')) return;
    try {
      await challengesService.rejectChallenge(challengeId);
      await loadChallenges();
      alert('Desafio rejeitado.');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao rejeitar desafio');
    }
  };

  const handleCancelChallenge = async (challengeId) => {
    if (!window.confirm('Tem certeza que deseja cancelar este desafio?')) return;
    try {
      await challengesService.cancelChallenge(challengeId);
      await loadChallenges();
      alert('Desafio cancelado.');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao cancelar desafio');
    }
  };

  const handleEditReward = async (reward) => {
    try {
      // Carrega as crianças que têm acesso a essa recompensa
      const response = await rewardsService.getRewardChildren(reward.id);
      const childrenIds = response.data.children.map(c => c.id);
      
      setEditingReward({
        ...reward,
        selectedChildren: childrenIds
      });
      setShowEditRewardForm(true);
    } catch (err) {
      console.error('Erro ao carregar dados da recompensa:', err);
      alert('Erro ao carregar dados da recompensa');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();

    try {
      await tasksService.createTask(
        child.id,
        newTask.title,
        newTask.description,
        newTask.points,
        newTask.category,
        newTask.frequency,
        newTask.parent_review_only
      );
      setNewTask({
        title: '',
        description: '',
        points: 10,
        category: 'CASA',
        frequency: 'DAILY',
        parent_review_only: false,
      });
      setShowNewTaskForm(false);
      await loadTasks();
    } catch (err) {
      console.error('Erro ao criar tarefa:', err);
    }
  };

  const handleEditTask = async (e) => {
    e.preventDefault();

    try {
      await tasksService.updateTask(editingTask.id, {
        title: editingTask.title,
        description: editingTask.description,
        points: editingTask.points,
        category: editingTask.category,
        frequency: editingTask.frequency,
        parent_review_only: editingTask.parent_review_only,
      });
      setEditingTask(null);
      setShowEditTaskForm(false);
      await loadTasks();
    } catch (err) {
      console.error('Erro ao atualizar tarefa:', err);
    }
  };

  const handleDeleteTask = async () => {
    try {
      await tasksService.deleteTask(taskToDelete.id);
      setTaskToDelete(null);
      setShowDeleteConfirm(false);
      await loadTasks();
    } catch (err) {
      console.error('Erro ao deletar tarefa:', err);
    }
  };

  const openEditTaskForm = (task) => {
    setEditingTask({ ...task });
    setShowEditTaskForm(true);
  };

  const openDeleteConfirm = (task) => {
    setTaskToDelete(task);
    setShowDeleteConfirm(true);
  };


  const handleCreateReward = async (e) => {
    e.preventDefault();

    try {
      // Cria a recompensa
      const response = await rewardsService.createReward(
        newReward.title,
        newReward.description,
        newReward.cost_points,
        newReward.min_age
      );
      
      const rewardId = response.data.reward.id;
      
      // Se nenhuma criança foi selecionada, associa a todas
      const childrenToAssign = newReward.selectedChildren.length > 0 
        ? newReward.selectedChildren 
        : allChildren.map(c => c.id);
      
      // Associa a recompensa às crianças selecionadas
      await Promise.all(
        childrenToAssign.map(childId => 
          rewardsService.addRewardToChild(rewardId, childId)
        )
      );
      
      setNewReward({
        title: '',
        description: '',
        cost_points: 50,
        min_age: null,
        selectedChildren: [],
      });
      setShowNewRewardForm(false);
      await loadRewards();
      alert('Recompensa criada e associada com sucesso!');
    } catch (err) {
      console.error('Erro ao criar recompensa:', err);
      alert('Erro ao criar recompensa: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdateReward = async (e) => {
    e.preventDefault();

    try {
      // Atualiza dados básicos da recompensa
      await rewardsService.updateReward(editingReward.id, {
        title: editingReward.title,
        description: editingReward.description,
        cost_points: editingReward.cost_points,
        min_age: editingReward.min_age
      });

      // Carrega crianças atualmente associadas
      const currentResponse = await rewardsService.getRewardChildren(editingReward.id);
      const currentChildrenIds = currentResponse.data.children.map(c => c.id);
      
      // Se nenhuma criança foi selecionada, considera como "todas"
      const targetChildren = editingReward.selectedChildren.length === 0 
        ? allChildren.map(c => c.id)
        : editingReward.selectedChildren;
      
      // Determina quais crianças adicionar e remover
      const toAdd = targetChildren.filter(id => !currentChildrenIds.includes(id));
      const toRemove = currentChildrenIds.filter(id => !targetChildren.includes(id));
      
      // Remove associações
      await Promise.all(
        toRemove.map(childId => 
          rewardsService.removeRewardFromChild(editingReward.id, childId)
        )
      );
      
      // Adiciona novas associações
      await Promise.all(
        toAdd.map(childId => 
          rewardsService.addRewardToChild(editingReward.id, childId)
        )
      );
      
      setShowEditRewardForm(false);
      setEditingReward(null);
      await loadRewards();
      alert('Recompensa atualizada com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar recompensa:', err);
      alert('Erro ao atualizar recompensa: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (window.confirm('Tem certeza que deseja deletar esta recompensa?')) {
      try {
        await rewardsService.deleteReward(rewardId);
        await loadRewards();
      } catch (err) {
        console.error('Erro ao deletar recompensa:', err);
      }
    }
  };

  const getTaskCompletion = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return null;

    const approved = taskCompletions
      .filter(c => c.task_id === taskId && c.status === 'APPROVED')
      .sort((a, b) => parseApiDateAsUtc(b.created_at) - parseApiDateAsUtc(a.created_at));

    if (approved.length === 0) return null;

    if (task.frequency === 'ONE_TIME') {
      return approved[0];
    }

    if (task.frequency === 'WEEKLY') {
      const now = new Date();
      const weekStart = new Date(now);
      const weekday = now.getDay();
      const diffToMonday = weekday === 0 ? -6 : 1 - weekday;
      weekStart.setDate(now.getDate() + diffToMonday);
      weekStart.setHours(0, 0, 0, 0);
      return approved.find(c => parseApiDateAsUtc(c.created_at) >= weekStart) || null;
    }

    // DAILY (default)
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    return approved.find(c => parseApiDateAsUtc(c.created_at) >= dayStart) || null;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING_AI': { text: '⏳ Pendente IA', class: 'status-pending-ai' },
      'PENDING_PARENT': { text: '👀 Aguardando Revisão', class: 'status-pending-parent' },
      'APPROVED': { text: '✅ Aprovado', class: 'status-approved' },
      'REJECTED_AI': { text: '❌ Rejeitado (IA)', class: 'status-rejected' },
      'REJECTED_PARENT': { text: '❌ Rejeitado', class: 'status-rejected' }
    };
    return statusMap[status] || { text: 'Desconhecido', class: 'status-unknown' };
  };

  const visibleTasks = taskFilter === 'all'
    ? tasks
    : tasks.filter((task) => !getTaskCompletion(task.id));

  const handleApproveCompletion = async (completionId) => {
    try {
      const feedback = feedbackMap[completionId] || '';
      await completionsService.approveCompletion(completionId, feedback);
      // Remove feedback do estado após enviar
      setFeedbackMap(prev => {
        const newMap = { ...prev };
        delete newMap[completionId];
        return newMap;
      });
      await loadPendingCompletions();
    } catch (err) {
      console.error('Erro ao aprovar conclusão:', err);
      alert('Erro ao aprovar conclusão: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRejectCompletion = async (completionId) => {
    try {
      const feedback = feedbackMap[completionId] || '';
      if (!feedback.trim()) {
        if (!window.confirm('Deseja rejeitar sem feedback? É recomendado explicar o motivo da rejeição para a criança.')) {
          return;
        }
      }
      await completionsService.rejectCompletion(completionId, feedback);
      // Remove feedback do estado após enviar
      setFeedbackMap(prev => {
        const newMap = { ...prev };
        delete newMap[completionId];
        return newMap;
      });
      await loadPendingCompletions();
    } catch (err) {
      console.error('Erro ao rejeitar conclusão:', err);
      alert('Erro ao rejeitar conclusão: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleGeneratePairingCode = async () => {
    setPairingLoading(true);
    try {
      const response = await api.post('/auth/parent/pairing-code');
      const data = response.data;
      setPairingCode(data.pairing_code);
      setPairingCodeExpiry(new Date(data.expires_at));
      // Timer para limpar código após expiração
      setTimeout(() => {
        setPairingCode(null);
        setPairingCodeExpiry(null);
      }, 15 * 60 * 1000);
    } catch (err) {
      console.error('Erro ao gerar código de pareamento:', err);
      const errorMsg = err.response?.data?.error || 'Erro ao gerar código';
      alert(errorMsg);
    } finally {
      setPairingLoading(false);
    }
  };

  const handleCopyPairingCode = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode);
      alert('Código copiado! Compartilhe com a criança.');
    }
  };

  const getRemainingTime = () => {
    if (!pairingCodeExpiry) return '';
    const now = new Date();
    const diff = pairingCodeExpiry - now;
    if (diff <= 0) return 'Expirado';
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  return (
    <div className="child-panel">
      <div className="child-header">
        <h2>{child.name}</h2>
        <div className="child-stats">
          <span>⭐ HeroCoins: {child.available_points}</span>
          <span>🔥 Streak: {child.streak_count}</span>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tasks')}
          data-icon="📋"
          aria-label="Tarefas"
        >
          Tarefas
        </button>
        <button
          className={`tab-btn ${activeTab === 'review' ? 'active' : ''}`}
          onClick={() => setActiveTab('review')}
          data-icon="✅"
          aria-label="Revisar tarefas"
        >
          Revisar
          {completions.filter(c => c.status === 'PENDING').length > 0 && (
            <span className="badge-count">{completions.filter(c => c.status === 'PENDING').length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => setActiveTab('rewards')}
          data-icon="🎁"
          aria-label="Recompensas"
        >
          Recompensas
        </button>
        <button
          className={`tab-btn ${activeTab === 'challenges' ? 'active' : ''}`}
          onClick={() => setActiveTab('challenges')}
          data-icon="⚔️"
          aria-label="Desafios"
        >
          Desafios
          {challenges.filter(c => c.status === 'SENT').length > 0 && (
            <span className="badge-count">{challenges.filter(c => c.status === 'SENT').length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'redemptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('redemptions')}
          data-icon="💰"
          aria-label="Resgates pendentes"
        >
          Resgates
          {redemptions.filter(r => r.status === 'PENDING').length > 0 && (
            <span className="badge-count">{redemptions.filter(r => r.status === 'PENDING').length}</span>
          )}
        </button>
        <button
          className={`tab-btn ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
          data-icon="📅"
          aria-label="Calendário"
        >
          Calendário
        </button>
        <button
          className={`tab-btn ${activeTab === 'pairing' ? 'active' : ''}`}
          onClick={() => setActiveTab('pairing')}
          data-icon="🔐"
          aria-label="Código de acesso"
        >
          Acesso
        </button>
      </div>

      {activeTab === 'tasks' && (
        <div className="tab-content">
          <button className="add-task-btn" onClick={() => setShowNewTaskForm(!showNewTaskForm)}>
            + Nova Tarefa
          </button>

          {showNewTaskForm && (
            <form onSubmit={handleCreateTask} className="task-form">
              <input
                type="text"
                placeholder="Título da tarefa"
                required
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              />
              <textarea
                placeholder="Descrição (opcional)"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              ></textarea>
              <input
                type="number"
                placeholder="HeroCoins"
                value={newTask.points}
                onChange={(e) => setNewTask({ ...newTask, points: parseInt(e.target.value) })}
                min="1"
              />
              <select
                value={newTask.category}
                onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
              >
                <option value="CASA">Casa</option>
                <option value="ESTUDOS">Estudos</option>
                <option value="HIGIENE">Higiene</option>
                <option value="RESPONSABILIDADE">Responsabilidade</option>
                <option value="OUTROS">Outros</option>
              </select>
              <select
                value={newTask.frequency}
                onChange={(e) => setNewTask({ ...newTask, frequency: e.target.value })}
              >
                <option value="DAILY">Diário</option>
                <option value="WEEKLY">Semanal</option>
                <option value="ONE_TIME">Uma vez</option>
              </select>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newTask.parent_review_only}
                  onChange={(e) => setNewTask({ ...newTask, parent_review_only: e.target.checked })}
                />
                <span>Análise feita somente pelo responsável (não usar IA)</span>
              </label>
              <button type="submit">Criar Tarefa</button>
            </form>
          )}

          {showEditTaskForm && editingTask && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Editar Tarefa</h3>
                <form onSubmit={handleEditTask} className="task-form">
                  <input
                    type="text"
                    placeholder="Título da tarefa"
                    required
                    value={editingTask.title}
                    onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                  />
                  <textarea
                    placeholder="Descrição (opcional)"
                    value={editingTask.description}
                    onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                  ></textarea>
                  <input
                    type="number"
                    placeholder="HeroCoins"
                    value={editingTask.points}
                    onChange={(e) => setEditingTask({ ...editingTask, points: parseInt(e.target.value) })}
                    min="1"
                  />
                  <select
                    value={editingTask.category}
                    onChange={(e) => setEditingTask({ ...editingTask, category: e.target.value })}
                  >
                    <option value="CASA">Casa</option>
                    <option value="ESTUDOS">Estudos</option>
                    <option value="HIGIENE">Higiene</option>
                    <option value="RESPONSABILIDADE">Responsabilidade</option>
                    <option value="OUTROS">Outros</option>
                  </select>
                  <select
                    value={editingTask.frequency}
                    onChange={(e) => setEditingTask({ ...editingTask, frequency: e.target.value })}
                  >
                    <option value="DAILY">Diário</option>
                    <option value="WEEKLY">Semanal</option>
                    <option value="ONE_TIME">Uma vez</option>
                  </select>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingTask.parent_review_only || false}
                      onChange={(e) => setEditingTask({ ...editingTask, parent_review_only: e.target.checked })}
                    />
                    <span>Análise feita somente pelo responsável (não usar IA)</span>
                  </label>
                  <div className="modal-actions">
                    <button type="submit">Salvar</button>
                    <button type="button" onClick={() => { setShowEditTaskForm(false); setEditingTask(null); }}>
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="tasks-list">
            <div className="task-filter-bar">
              <span className="task-filter-label">Mostrar</span>
              <select
                className="task-filter-select"
                value={taskFilter}
                onChange={(e) => setTaskFilter(e.target.value)}
                aria-label="Filtrar tarefas"
              >
                <option value="pending">Somente pendentes</option>
                <option value="all">Pendentes e finalizadas</option>
              </select>
            </div>

            {visibleTasks.length > 0 ? (
              visibleTasks.map((task) => {
                const completion = getTaskCompletion(task.id);
                const statusInfo = completion ? getStatusBadge(completion.status) : null;
                return (
                  <div key={task.id} className="task-card">
                    <div className="task-header">
                      <h4>{task.title}</h4>
                      <div className="task-actions">
                        {task.study_mission_id && (
                          <button
                            className="view-mission-btn"
                            onClick={() => navigate(`/parent/study-missions/${task.study_mission_id}?readonly=true`)}
                            title="Ver Missão de Estudo (somente leitura)"
                          >
                            📚
                          </button>
                        )}
                        <button
                          className="edit-btn"
                          onClick={() => openEditTaskForm(task)}
                          title="Editar tarefa"
                        >
                          ✏️
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => openDeleteConfirm(task)}
                          title="Deletar tarefa"
                        >
                          🗑️
                        </button>
                      </div>
                      {statusInfo && (
                        <span className={`status-badge ${statusInfo.class}`}>
                          {statusInfo.text}
                        </span>
                      )}
                      {!statusInfo && (
                        <span className="status-badge status-not-started">📝 Não iniciada</span>
                      )}
                    </div>
                    <p>{task.description}</p>
                    <div className="task-meta">
                      <span>⭐ {task.points} HeroCoins</span>
                      <span>{task.category}</span>
                      <span>{task.frequency}</span>
                      {task.parent_review_only && (
                        <span className="manual-review-badge" title="Esta tarefa será revisada manualmente pelo responsável, sem análise de IA">
                          👤 Revisão Manual
                        </span>
                      )}
                    </div>
                    {completion && completion.status === 'APPROVED' && (
                      <div className="completion-date">
                        <span className="completion-timestamp">
                          ✅ Concluída em: {parseApiDateAsUtc(completion.created_at)?.toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                    {completion && completion.image_url && (
                      <div className="completion-preview">
                        <button
                          className="view-proof-btn"
                          onClick={() => window.open(getImageUrl(completion.image_url), '_blank')}
                          title="Clique para ver a comprovação em tamanho completo"
                        >
                          📸 Ver Comprovação
                        </button>
                        <img
                          src={getImageUrl(completion.image_url)}
                          alt="Comprovação"
                          className="completion-thumb"
                        />
                      </div>
                    )}
                    {completion && (completion.ai_status || completion.parent_comment) && (
                      <div className="review-info">
                        {completion.ai_status && (
                          <div className="ai-review">
                            <strong>🤖 Análise da IA:</strong>
                            <div className="ai-details">
                              <span className={`ai-result ${completion.ai_status.toLowerCase()}`}>
                                {completion.ai_status === 'APPROVED' ? '✅ Aprovada' : 
                                 completion.ai_status === 'REJECTED' ? '❌ Rejeitada' : 
                                 '⚠️ Inconclusiva'}
                              </span>
                              {completion.ai_confidence !== null && completion.ai_confidence !== undefined && (
                                <span className="ai-confidence">
                                  Confiança: {Math.round(completion.ai_confidence * 100)}%
                                </span>
                              )}
                            </div>
                            {completion.ai_comment && (
                              <p className="ai-comment">💬 {completion.ai_comment}</p>
                            )}
                          </div>
                        )}
                        {completion.parent_comment && (
                          <div className="parent-review">
                            <strong>👤 Revisão do Responsável:</strong>
                            <p className="parent-comment">{completion.parent_comment}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="empty">Nenhuma tarefa criada ainda</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="tab-content">
          <EventsCalendarTab
            mode="parent"
            childId={child.id}
            onViewTask={() => setActiveTab('tasks')}
            onViewStudyMission={(missionId) => navigate(`/parent/study-missions/${missionId}?readonly=true`)}
          />
        </div>
      )}

      {showDeleteConfirm && taskToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirmar Exclusão</h3>
            <p>Tem certeza que deseja deletar a tarefa "{taskToDelete.title}"?</p>
            <p className="warning-text">Esta ação não pode ser desfeita.</p>
            <div className="modal-actions">
              <button onClick={handleDeleteTask} className="confirm-delete-btn">
                Deletar
              </button>
              <button onClick={() => { setShowDeleteConfirm(false); setTaskToDelete(null); }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'challenges' && (
        <div className="tab-content">
          <button className="add-task-btn" onClick={() => setShowNewChallengeForm(!showNewChallengeForm)}>
            ⚔️ Criar Desafio para {child.name}
          </button>

          {showNewChallengeForm && (
            <form onSubmit={handleCreateChallenge} className="task-form">
              <input
                type="text"
                placeholder="Título do desafio"
                required
                value={newChallenge.title}
                onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })}
              />
              <textarea
                placeholder="Descrição (opcional)"
                value={newChallenge.description}
                onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })}
              ></textarea>
              <input
                type="number"
                placeholder="HeroCoins"
                value={newChallenge.points}
                onChange={(e) => setNewChallenge({ ...newChallenge, points: e.target.value })}
                min="1"
                max="300"
                required
              />
              <div className="form-actions">
                <button type="submit">Enviar Desafio</button>
                <button type="button" onClick={() => setShowNewChallengeForm(false)}>Cancelar</button>
              </div>
            </form>
          )}

          <div className="challenges-section">
            <h3>📥 Recebidos (de {child.name})</h3>
            <div className="challenges-list">
              {challenges.filter(c => c.created_by_role === 'CHILD' && c.status === 'SENT').length > 0 ? (
                challenges.filter(c => c.created_by_role === 'CHILD' && c.status === 'SENT').map((challenge) => (
                  <div key={challenge.id} className="challenge-card">
                    <div className="challenge-header">
                      <h4>{challenge.title}</h4>
                      <span className="challenge-points">⭐ {challenge.points} HeroCoins</span>
                    </div>
                    {challenge.description && <p className="challenge-description">{challenge.description}</p>}
                    <div className="challenge-meta">
                      <span>📅 {new Date(challenge.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <div className="challenge-actions">
                      <button className="accept-btn" onClick={() => handleAcceptChallenge(challenge.id)}>✅ Aceitar</button>
                      <button className="reject-btn" onClick={() => handleRejectChallenge(challenge.id)}>❌ Rejeitar</button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty">Nenhum desafio recebido pendente</p>
              )}
            </div>
          </div>

          <div className="challenges-section">
            <h3>📤 Enviados (para {child.name})</h3>
            <div className="challenges-list">
              {challenges.filter(c => c.created_by_role === 'PARENT').length > 0 ? (
                challenges.filter(c => c.created_by_role === 'PARENT').map((challenge) => (
                  <div key={challenge.id} className="challenge-card">
                    <div className="challenge-header">
                      <h4>{challenge.title}</h4>
                      <span className="challenge-points">⭐ {challenge.points} HeroCoins</span>
                    </div>
                    {challenge.description && <p className="challenge-description">{challenge.description}</p>}
                    <div className="challenge-meta">
                      <span>📅 {new Date(challenge.created_at).toLocaleDateString('pt-BR')}</span>
                      <span className={`challenge-status status-${challenge.status.toLowerCase()}`}>
                        {challenge.status === 'SENT' && '⏳ Aguardando'}
                        {challenge.status === 'ACCEPTED' && '✅ Aceito'}
                        {challenge.status === 'REJECTED' && '❌ Rejeitado'}
                        {challenge.status === 'CANCELLED' && '🚫 Cancelado'}
                      </span>
                    </div>
                    {challenge.status === 'SENT' && (
                      <div className="challenge-actions">
                        <button className="cancel-btn" onClick={() => handleCancelChallenge(challenge.id)}>Cancelar</button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="empty">Nenhum desafio enviado ainda</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'review' && (
        <div className="tab-content">
          <div className="completions-list">
            {completions.length > 0 ? (
              completions.map((completion) => {
                const task = tasks.find(t => t.id === completion.task_id);
                return (
                  <div key={completion.id} className="completion-card">
                    <div className="completion-header">
                      <h4>{task?.title || 'Tarefa'}</h4>
                      {task?.description && (
                        <p className="task-description">{task.description}</p>
                      )}
                    </div>
                    {completion.image_url && (
                      <img
                        src={getImageUrl(completion.image_url)}
                        alt="Conclusão"
                        className="completion-image"
                      />
                    )}
                    {completion.ai_status && (
                      <div className="ai-info">
                        <strong>🤖 Análise da IA:</strong>
                        <div className="ai-details">
                          <span className={`ai-badge ${completion.ai_status.toLowerCase()}`}>
                            {completion.ai_status === 'APPROVED' ? '✅ Aprovada' : 
                             completion.ai_status === 'REJECTED' ? '❌ Rejeitada' : 
                             '⚠️ Inconclusiva'}
                          </span>
                          <span className="ai-confidence">
                            Confiança: {Math.round(completion.ai_confidence * 100)}%
                          </span>
                        </div>
                        {completion.ai_comment && (
                          <p className="ai-comment">{completion.ai_comment}</p>
                        )}
                      </div>
                    )}
                    <div className="feedback-section">
                      <label htmlFor={`feedback-${completion.id}`}>
                        <strong>💬 Seu feedback para a criança:</strong>
                      </label>
                      <textarea
                        id={`feedback-${completion.id}`}
                        placeholder="Escreva um feedback para a criança (ex: 'Parabéns! Muito bem feito!' ou 'Tente novamente, a cama não está bem arrumada')..."
                        value={feedbackMap[completion.id] || ''}
                        onChange={(e) => setFeedbackMap(prev => ({
                          ...prev,
                          [completion.id]: e.target.value
                        }))}
                        rows={3}
                        className="feedback-textarea"
                      />
                    </div>
                    <div className="completion-actions">
                      <button
                        className="approve-btn"
                        onClick={() => handleApproveCompletion(completion.id)}
                      >
                        ✓ Aprovar
                      </button>
                      <button
                        className="reject-btn"
                        onClick={() => handleRejectCompletion(completion.id)}
                      >
                        ✗ Rejeitar
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="empty">Nenhuma conclusão pendente</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'rewards' && (
        <div className="tab-content">
          <button className="add-reward-btn" onClick={() => setShowNewRewardForm(!showNewRewardForm)}>
            + Nova Recompensa
          </button>

          {showNewRewardForm && (
            <form onSubmit={handleCreateReward} className="reward-form">
              <input
                type="text"
                placeholder="Título da recompensa"
                value={newReward.title}
                onChange={(e) => setNewReward({ ...newReward, title: e.target.value })}
                required
              />
              <textarea
                placeholder="Descrição (opcional)"
                value={newReward.description}
                onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
              ></textarea>
              <input
                type="number"
                placeholder="Custo em HeroCoins"
                value={newReward.cost_points}
                onChange={(e) => setNewReward({ ...newReward, cost_points: parseInt(e.target.value) })}
                min="1"
                required
              />
              <input
                type="number"
                placeholder="Idade mínima (opcional)"
                value={newReward.min_age || ''}
                onChange={(e) => setNewReward({ ...newReward, min_age: e.target.value ? parseInt(e.target.value) : null })}
                min="0"
              />
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Disponível para:
                </label>
                {allChildren.map(childItem => (
                  <label key={childItem.id} style={{ display: 'block', marginBottom: '5px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newReward.selectedChildren.includes(childItem.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewReward({
                            ...newReward,
                            selectedChildren: [...newReward.selectedChildren, childItem.id]
                          });
                        } else {
                          setNewReward({
                            ...newReward,
                            selectedChildren: newReward.selectedChildren.filter(id => id !== childItem.id)
                          });
                        }
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {childItem.name}
                  </label>
                ))}
                {newReward.selectedChildren.length === 0 && (
                  <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    ℹ️ Nenhuma criança selecionada = disponível para todas
                  </p>
                )}
              </div>
              <button type="submit">Criar Recompensa</button>
              <button type="button" onClick={() => setShowNewRewardForm(false)} style={{ marginLeft: '10px', backgroundColor: '#999' }}>
                Cancelar
              </button>
            </form>
          )}

          {showEditRewardForm && editingReward && (
            <form onSubmit={handleUpdateReward} className="reward-form" style={{ backgroundColor: '#fff3cd', border: '2px solid #ffc107' }}>
              <h3 style={{ marginTop: 0, color: '#856404' }}>✏️ Editar Recompensa</h3>
              <input
                type="text"
                placeholder="Título da recompensa"
                value={editingReward.title}
                onChange={(e) => setEditingReward({ ...editingReward, title: e.target.value })}
                required
              />
              <textarea
                placeholder="Descrição (opcional)"
                value={editingReward.description}
                onChange={(e) => setEditingReward({ ...editingReward, description: e.target.value })}
              ></textarea>
              <input
                type="number"
                placeholder="Custo em HeroCoins"
                value={editingReward.cost_points}
                onChange={(e) => setEditingReward({ ...editingReward, cost_points: parseInt(e.target.value) })}
                min="1"
                required
              />
              <input
                type="number"
                placeholder="Idade mínima (opcional)"
                value={editingReward.min_age || ''}
                onChange={(e) => setEditingReward({ ...editingReward, min_age: e.target.value ? parseInt(e.target.value) : null })}
                min="0"
              />
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Disponível para:
                </label>
                {allChildren.map(childItem => (
                  <label key={childItem.id} style={{ display: 'block', marginBottom: '5px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingReward.selectedChildren.includes(childItem.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditingReward({
                            ...editingReward,
                            selectedChildren: [...editingReward.selectedChildren, childItem.id]
                          });
                        } else {
                          setEditingReward({
                            ...editingReward,
                            selectedChildren: editingReward.selectedChildren.filter(id => id !== childItem.id)
                          });
                        }
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {childItem.name}
                  </label>
                ))}
                {editingReward.selectedChildren.length === 0 && (
                  <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    ℹ️ Nenhuma criança selecionada = disponível para todas
                  </p>
                )}
              </div>
              <button type="submit" style={{ backgroundColor: '#28a745' }}>Salvar Alterações</button>
              <button type="button" onClick={() => {
                setShowEditRewardForm(false);
                setEditingReward(null);
              }} style={{ marginLeft: '10px', backgroundColor: '#999' }}>
                Cancelar
              </button>
            </form>
          )}

          <div className="rewards-list">
            {rewards.length > 0 ? (
              rewards.map((reward) => (
                <div key={reward.id} className="reward-card">
                  <div className="reward-info">
                    <h4>{reward.title}</h4>
                    <p>{reward.description}</p>
                    <div className="reward-meta">
                      <span>💰 {reward.cost_points} HeroCoins</span>
                      {reward.min_age && <span>📅 Mínimo {reward.min_age} anos</span>}
                    </div>
                  </div>
                  <div className="reward-actions">
                    <button
                      className="edit-btn"
                      onClick={() => handleEditReward(reward)}
                      title="Editar recompensa"
                    >
                      ✏️
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteReward(reward.id)}
                      title="Deletar recompensa"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty">Nenhuma recompensa criada ainda</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'redemptions' && (
        <div className="tab-content">
          <RedemptionsTab child={child} redemptions={redemptions} onDeliver={loadPendingRedemptions} />
        </div>
      )}

      {activeTab === 'pairing' && (
        <div className="tab-content">
          <div style={{ maxWidth: '500px', margin: '20px auto', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0 }}>🔐 Código de Acesso para {child.name}</h3>
            <p>Compartilhe este código com a criança para ela fazer login no próprio celular.</p>
            <p style={{ color: '#666', fontSize: '0.9em' }}>O código é válido por 15 minutos e pode ser regenerado conforme necessário.</p>
            
            {pairingCode ? (
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '8px', 
                textAlign: 'center',
                marginBottom: '15px'
              }}>
                <div style={{ 
                  fontSize: '2.5em', 
                  fontWeight: 'bold', 
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  color: '#1976d2',
                  marginBottom: '10px',
                  userSelect: 'all'
                }}>
                  {pairingCode}
                </div>
                <div style={{ 
                  fontSize: '0.9em', 
                  color: pairingCodeExpiry && getRemainingTime() === 'Expirado' ? '#d32f2f' : '#1976d2',
                  fontWeight: 'bold',
                  marginBottom: '15px'
                }}>
                  ⏱️ Expira em: {getRemainingTime()}
                </div>
                <button
                  onClick={handleCopyPairingCode}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1em',
                    fontWeight: 'bold'
                  }}
                >
                  📋 Copiar Código
                </button>
              </div>
            ) : null}
            
            <button
              onClick={handleGeneratePairingCode}
              disabled={pairingLoading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: pairingCode ? '#ff9800' : '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: pairingLoading ? 'not-allowed' : 'pointer',
                fontSize: '1em',
                fontWeight: 'bold',
                opacity: pairingLoading ? 0.6 : 1
              }}
            >
              {pairingLoading ? '⏳ Gerando...' : pairingCode ? '🔄 Gerar Novo Código' : '🔓 Gerar Código'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RedemptionsTab({ child, redemptions, onDeliver }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDeliver = async (redemptionId) => {
    setLoading(true);
    setError('');

    try {
      await rewardsService.deliverRedemption(redemptionId);
      alert('✅ Recompensa marcada como entregue!');
      onDeliver();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao marcar entrega');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="redemptions-tab">
      {error && <div className="error-message">{error}</div>}

      {redemptions.length > 0 ? (
        <div className="redemptions-list">
          {redemptions.map((redemption) => (
            <div key={redemption.id} className="redemption-card">
              <div className="redemption-info">
                <h4>{redemption.reward?.title || 'Recompensa desconhecida'}</h4>
                <p>{redemption.reward?.description}</p>
                <small className="redemption-date">
                  Resgatado em: {new Date(redemption.created_at).toLocaleDateString('pt-BR')}
                </small>
              </div>

              <div className="redemption-status">
                <span className="status-badge pending">⏳ Aguardando Entrega</span>
              </div>

              <button
                className="deliver-btn"
                onClick={() => handleDeliver(redemption.id)}
                disabled={loading}
              >
                {loading ? '⏳' : '📦'} Marcar como Entregue
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty">Nenhuma recompensa pendente de entrega para {child.name}</p>
      )}
    </div>
  );
}
